// The ACTIVE persona (who a lens holder is currently viewing as), persisted.
//
// Pure JS — no React, no DOM, no better-sqlite3 — because both sides of the app
// need it: the server layout reads the cookie to seed PersonaProvider, the root
// route reads it to pick a landing page, and the topbar writes it from the
// browser. A cookie rather than localStorage precisely so the SERVER can see it:
// the persona is decided before the first byte of HTML, so a reload lands you
// where you were with no flash of the admin console on the way.
//
// THE COOKIE HOLDS A LENS KEY, NOT A PERSONA: `admin`, the person's slug
// (`maya-lin`), or `brand:<slug>` (`brand:fernhill`). It used to hold the whole
// persona as JSON. A key is a NAME for one of the lenses the server offers, so
// everything the lens scopes by comes from the roster at read time and none of
// it from the browser. The same key is what `?as=` carries
// (lib/lens-url.ts), which is why no email is in it.
//
// THIS IS UNTRUSTED INPUT. It is user-editable by definition, so:
//
//   1. It is only ever honoured for an identity that holds a lens — an admin,
//      or a staff seat over their own brands — checked against the session,
//      server-side, in activeLensFor. A creator who hand-writes `admin` gets
//      nothing, and neither does an AM: the cookie chooses a VIEW, never a
//      privilege, and isAdmin has never come from here.
//   2. It is resolved against the lenses the server actually offers
//      (`switchablePersonas()`), and a key that names none of them is no lens at
//      all: the viewer lands on their identity. A key can only SELECT a lens the
//      roster built. It cannot describe one, which is what a forged JSON cookie
//      could try to do and what the JSON path's roster check exists to refuse.

import type { Persona, SwitchableLens } from "@/lib/persona-types";

export const PERSONA_COOKIE = "ironside-active-persona";

/** 30 days. Long enough to feel permanent; short enough that a stale view expires. */
export const PERSONA_COOKIE_MAX_AGE = 60 * 60 * 24 * 30;

export const ADMIN_LENS_KEY = "admin";

/**
 * A BRAND LENS KEEPS ITS PREFIX because a brand slug and a person's slug come
 * from different tables and can be the same word. A person's slug never holds
 * `:` (lib/slug.ts), so `brand:<slug>` cannot be anybody's slug.
 */
const BRAND_LENS_PREFIX = "brand:";

/** What a person's slug may look like — lib/slug.ts' `slugify` mints only this. */
const PERSON_SLUG = /^[a-z0-9-]+$/;

/**
 * The key that names this lens, or null when it cannot have one.
 *
 *   admin                                         → `admin`
 *   am, am_ops, creative_specialist,
 *   creator_expert, creator, writer               → the person's slug
 *   brand, holding exactly one brand              → `brand:<brand slug>`
 *   brand, holding none or several                → null
 *
 * `slugFor` is the server's `slugForEmail` (lib/server/user-cache.ts). It is an
 * argument rather than an import so this module stays pure.
 *
 * ⚠️ NULL IS THE ANSWER FOR A PERSON WITH NO SLUG, never the email. Every
 * switchable person comes from a `users` row and the boot backfill mints every
 * row a slug, so this "cannot" happen — which is exactly when a fallback to the
 * address would ship unnoticed and put an email in the cookie and in the URL.
 * `switchablePersonas()` offers no lens for a null key instead. A value that is
 * not slug-shaped is refused the same way, so the rule is proved here rather
 * than trusted to the minting code.
 *
 * A BRAND LENS IS KEYED BY ITS SLUG, because its email can be empty: a brand
 * nobody has been provisioned against yet has a lens and no contact (see the
 * `brand` case in personaFromShape). The key form has no email in it to be empty.
 *
 * A BRAND PERSONA WITH TWO OR MORE BRANDS HAS NO KEY. It is only ever a
 * signed-in brand user's own identity — every brand lens is one brand, minted
 * per brand by `switchablePersonas()` — and that identity holds no lens, so
 * nothing reads a key off it. Keying it would mint a lens nobody can pick.
 *
 * A WRITER IS THEIR OWN SLUG, with nothing about the creator they write for.
 * The JSON cookie had to carry that creator and the roster check had to compare
 * it, or a hand edit could re-point a real writer at somebody else's desk. A key
 * only selects: the writer's creator comes from the lens the server built.
 */
export function lensKey(p: Persona, slugFor: (email: string) => string | null): string | null {
  if (p.role === "admin") return ADMIN_LENS_KEY;
  if (p.role === "brand") return p.brands.length === 1 && p.brands[0] ? `${BRAND_LENS_PREFIX}${p.brands[0]}` : null;
  const slug = slugFor(p.email);
  return slug && PERSON_SLUG.test(slug) && slug !== ADMIN_LENS_KEY ? slug : null;
}

/**
 * The lens a key names, out of the lenses the server offered — or null for a key
 * that names none of them (a forged one, a deleted person's, an archived
 * brand's). null means "no lens": the caller falls back to the identity.
 *
 * ⚠️ THIS IS THE WHOLE CHECK, and it is a lookup on purpose. The persona that
 * comes back is the server's own, so nothing the key's author typed reaches the
 * scope. Hand it the list `switchablePersonas()` returns, never a list built
 * from anything a request carries.
 */
export function personaForKey(
  key: string | null | undefined,
  switchable: readonly SwitchableLens[],
): Persona | null {
  if (!key) return null;
  return switchable.find((l) => l.key === key)?.persona ?? null;
}

/**
 * The cookie's value for a key, encoded as a cookie value is. A key is
 * `[a-z0-9-:]`, so this only ever changes the brand prefix's `:`; it is encoded
 * anyway so that Next's parser (which decodes once) and lib/stale-lens.ts' reader
 * (which decodes once) read back exactly the key that was written.
 */
export function lensCookieValue(key: string): string {
  return encodeURIComponent(key);
}

const isStr = (v: unknown): v is string => typeof v === "string" && v.length > 0;
const isStrArr = (v: unknown): v is string[] => Array.isArray(v) && v.every((x) => typeof x === "string");

/**
 * Shape-check an already-parsed value into a Persona. null for anything that is
 * not one: the wrong role, a missing field, a field of the wrong type.
 *
 * A well-formed persona naming somebody who does not exist still passes here —
 * shape is all this can know. The roster check in lensFromCookie is what
 * rejects it.
 *
 * Shared with the dev-auth bypass (lib/server/auth-dev.ts), which reads a persona
 * out of an environment variable and has exactly the same problem: hand-written
 * JSON that must not become a half-built persona. A creator carrying the old
 * `"brand": "x"` shape now fails here rather than reaching a page as an object
 * with no `brands` to read.
 */
export function personaFromShape(value: unknown): Persona | null {
  if (typeof value !== "object" || value === null) return null;
  const p = value as Record<string, unknown>;

  switch (p.role) {
    case "admin":
      return { role: "admin" };
    case "am":
      return isStr(p.email) && isStr(p.displayName) && isStrArr(p.brands)
        ? { role: "am", email: p.email, displayName: p.displayName, brands: p.brands }
        : null;
    case "am_ops":
      // The AM's shape: the brands are the reach, and they were derived from the
      // covered AMs server-side. lensFromCookie still checks the list against
      // what the roster actually offers.
      return isStr(p.email) && isStr(p.displayName) && isStrArr(p.brands)
        ? { role: "am_ops", email: p.email, displayName: p.displayName, brands: p.brands }
        : null;
    case "creative_specialist":
      // The AM's shape too: their brands are their own picked rows, and
      // lensFromCookie checks the list against what the roster offers.
      return isStr(p.email) && isStr(p.displayName) && isStrArr(p.brands)
        ? { role: "creative_specialist", email: p.email, displayName: p.displayName, brands: p.brands }
        : null;
    case "creator_expert":
      // NO BRANDS TO CHECK, and none to accept either — a hand-written cookie
      // carrying a `brands` array for this role parses into a persona without
      // one, because the union has no such field and nothing downstream would
      // ever read it. Their scope is their nav (two agency-wide pages), which
      // canOpen decides from the role alone.
      return isStr(p.email) && isStr(p.displayName)
        ? { role: "creator_expert", email: p.email, displayName: p.displayName }
        : null;
    case "creator":
      // brands, PLURAL — a creator works as many as they are staffed onto. An
      // empty list is legal: an unassigned creator sees no brand at all.
      //
      // `scopeBrands` IS NEVER READ FROM HERE, for a creator or a writer. A
      // narrowed lens is only ever the roster's own object, narrowing and all;
      // nothing a cookie or an env var spells out may describe one.
      return isStr(p.email) && isStr(p.displayName) && isStrArr(p.brands)
        ? { role: "creator", email: p.email, displayName: p.displayName, brands: p.brands }
        : null;
    case "writer":
      // creatorEmail is REQUIRED and non-empty, unlike a brand lens's email
      // below. It is the id every script of theirs is filtered by, so a writer
      // persona without one scopes to nothing at all — an empty Scripts page
      // with no error on it. There is no such thing as an unattached writer;
      // personaFor() refuses to mint one, and this refuses to parse one.
      return isStr(p.email) && isStr(p.displayName) && isStr(p.creatorEmail) &&
        isStr(p.creatorName) && isStrArr(p.brands)
        ? {
            role: "writer",
            email: p.email,
            displayName: p.displayName,
            creatorEmail: p.creatorEmail,
            creatorName: p.creatorName,
            brands: p.brands,
          }
        : null;
    case "brand":
      // A BRAND LENS IS IDENTIFIED BY ITS SLUG, AND ITS EMAIL MAY BE EMPTY.
      //
      // switchablePersonas mints one lens per brand it offers and fills the email
      // from that brand's contact user — `contact?.email ?? ''` — so a brand
      // nobody has been provisioned against yet gets a lens with an empty email.
      // That is a normal, intended state: an admin has to be able to view as a
      // brand before its people exist, and every brand in the prod seed is in it.
      //
      // Requiring a non-empty email here made the cookie UNPARSEABLE for exactly
      // those lenses. The switch worked — it is React state — and then the next
      // hard reload silently dropped back to Admin, because the server read the
      // cookie it had just written, shape-checked it, got null, and fell through
      // to the identity. Nothing errored; the lens just would not stick, and only
      // on brands with no contact, which is why it read as "impersonation resets
      // on reload" rather than as a bug.
      //
      // So: the shape check must accept exactly what the server can mint. The
      // slugs are what carry the scope (scriptInScope filters on p.brands, and
      // never on a brand's email), and they are still required.
      //
      // The cookie is a key now (`brand:<slug>`), which has no email in it to be
      // empty, so this case matters only to a JSON cookie from before the key —
      // and one such cookie, for a contactless brand, must still read back.
      // Those cookies say `brand: "<slug>"`, singular, from before a brand user
      // could hold a list; they read back as `brands: [slug]`, the shape of the
      // admin's one-brand lens they were written from.
      if (typeof p.email !== "string" || !isStr(p.displayName)) return null;
      if (isStrArr(p.brands)) return { role: "brand", email: p.email, displayName: p.displayName, brands: p.brands };
      return isStr(p.brand) ? { role: "brand", email: p.email, displayName: p.displayName, brands: [p.brand] } : null;
    default:
      return null;
  }
}

/**
 * THE LENS A PERSONA COOKIE NAMES, resolved against the lenses the server
 * offered. null for anything that names none of them — absent, malformed,
 * forged, or naming somebody who has since been deleted — and the caller falls
 * back to the identity. Nothing is lost: the cookie holds a view preference, not
 * a credential.
 *
 * TWO FORMATS, FOR ONE COOKIE LIFETIME. The cookie is a lens key now; until this
 * change it was the persona as JSON, and an admin who has not switched since the
 * deploy still holds that. Dropping it would put every such admin back on the
 * console on the first load after the deploy, so the JSON is still read. ⚠️
 * DELETE THE JSON PATH ONE `PERSONA_COOKIE_MAX_AGE` (30 days) AFTER THIS SHIPS:
 * every cookie the app has written since is a key, and a JSON cookie older than
 * that has expired in the browser. It is told apart by its first character — a
 * key starts with a letter or digit, and JSON with `{`.
 *
 * ⚠️ `value` IS WHAT `cookies().get(PERSONA_COOKIE)?.value` RETURNS, ALREADY
 * DECODED. The cookie is written encoded, and Next's parser
 * (@edge-runtime/cookies) decodes it once on the way in. This used to decode it
 * a second time, on a comment saying Next handed back the raw header: a display
 * name with a `%` in it then threw ("100% Pod") or changed ("50%41" read as
 * "50A"), and an admin's lens fell back to admin with nothing on screen.
 * lib/stale-lens.ts's client reader decodes once too, so the two agree.
 */
export function lensFromCookie(
  value: string | undefined | null,
  switchable: readonly SwitchableLens[],
): Persona | null {
  if (!value) return null;
  if (!value.startsWith("{")) return personaForKey(value, switchable);

  // ── The JSON cookie, from before the key. See the ⚠️ above. ──
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return null; // a hand edit, or a value that was never ours
  }
  const claimed = personaFromShape(parsed);
  if (!claimed) return null;
  // The server's own persona, not the cookie's: they agree on every id by the
  // time this matches, and the roster's copy carries today's display name.
  const key = personaKey(claimed);
  return switchable.find((l) => personaKey(l.persona) === key)?.persona ?? null;
}

/**
 * The identity of a persona, for comparing a JSON cookie against the real roster.
 * Only the JSON path reads it (lensFromCookie); a key needs no comparing.
 *
 * A WRITER'S KEY CARRIES THE CREATOR THEY ARE ATTACHED TO, because that is the
 * field their whole scope is read through. Leave it out and a hand-edited cookie
 * could keep a writer's own (offered, therefore allowed) email while swapping
 * creatorEmail for somebody else's — and the roster check would wave it through.
 */
function personaKey(p: Persona): string {
  if (p.role === "admin") return "admin";
  if (p.role === "writer") return `writer:${p.email}:${p.creatorEmail}:${p.brands.join(",")}${scopeSuffix(p.scopeBrands)}`;
  // A CREATOR EXPERT IS THEIR EMAIL AND NOTHING ELSE, because that is the whole
  // of the persona — there is no scope on it to hand-edit, so there is none to
  // compare. The trailing empty segment keeps the shape uniform with the roles
  // below rather than saving four characters.
  if (p.role === "creator_expert") return `creator_expert:${p.email}:`;
  if (p.role === "creator") return `creator:${p.email}:${p.brands.join(",")}${scopeSuffix(p.scopeBrands)}`;
  return `${p.role}:${p.email}:${p.brands.join(",")}`;
}

/**
 * A NARROWED LENS NEVER MATCHES A JSON COOKIE. personaFromShape drops
 * `scopeBrands`, so a claimed persona has none, and a lens that carries some
 * keys differently. The JSON cookie predates every narrowed lens; one cannot
 * have been written for it.
 */
function scopeSuffix(scopeBrands: readonly string[] | undefined): string {
  return scopeBrands ? `:scope:${scopeBrands.join(",")}` : "";
}
